import React, { useState, useRef, useEffect } from 'react';
import { PlusCircle, Pencil, AlertCircle, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, X, Save, Check } from 'lucide-react';
import { Document, Page } from 'react-pdf';
import SignatureCanvas from './SignatureCanvas';
import axios from 'axios';

/**
 * SignaturePlacement component for adding and positioning signatures on documents
 */
const SignaturePlacement = ({ 
  documentData, 
  onSignatureAdded, 
  onCancel, 
  documentId, 
  user, 
  API_URL,
  signatureFields = [],
  selectedField = null
}) => {
  console.log('SignaturePlacement component rendered with props:');
  console.log('selectedField:', selectedField);
  console.log('signatureFields:', signatureFields);
  // Document state
  const [currentPage, setCurrentPage] = useState(selectedField?.page || 1);
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState(null);
  const [numPages, setNumPages] = useState(null);
  
  // Signature state - AUTO-SHOW signature canvas immediately
  const [showSignatureCanvas, setShowSignatureCanvas] = useState(true); // Always start with signature canvas
  const [signatureData, setSignatureData] = useState(null);
  const [signatureType, setSignatureType] = useState('drawn');
  
  // Initialize position based on selected field if available
  const [signaturePosition, setSignaturePosition] = useState({ 
    x: selectedField?.x || 0, 
    y: selectedField?.y || 0 
  });
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [prePlacedFields, setPrePlacedFields] = useState([]);
  const [activeFieldId, setActiveFieldId] = useState(selectedField?.id || null);
  
  // Refs
  const signatureRef = useRef(null);
  const documentRef = useRef(null);
  const initializedRef = useRef(false);
  
  // Track PDF page dimensions for coordinate conversion
  const [pdfPageDimensions, setPdfPageDimensions] = useState(null);
  const [renderedPageDimensions, setRenderedPageDimensions] = useState(null);
  
  // Use pre-placed fields if available, otherwise fetch them
  useEffect(() => {
    if (signatureFields && signatureFields.length > 0) {
      setPrePlacedFields(signatureFields);
    } else {
      fetchSignatureFields();
    }
  }, [signatureFields, documentId]);
  
  // ONLY position the signature when it's first created
  // And ONLY center it if no selected field is provided
  useEffect(() => {
    if (!initializedRef.current && documentRef.current && signatureData) {
      initializedRef.current = true;
      
      // If we have a selected field, position signature at that field
      if (selectedField) {
        setSignaturePosition({
          x: selectedField.x || 0,
          y: selectedField.y || 0
        });
      } 
      // If we have pre-placed fields for current page, use the first one
      else if (prePlacedFields.length > 0) {
        const fieldsOnCurrentPage = prePlacedFields.filter(field => 
          (field.page === currentPage) || (field.page === undefined && currentPage === 1)
        );
        
        if (fieldsOnCurrentPage.length > 0) {
          const field = fieldsOnCurrentPage[0];
          setSignaturePosition({
            x: field.x || 0,
            y: field.y || 0
          });
          setActiveFieldId(field.id);
        } else {
          // Center if no fields on this page
          centerSignature();
        }
      } else {
        // Center if no pre-placed fields
        centerSignature();
      }
    }
  }, [signatureData, selectedField, prePlacedFields, currentPage]);
  
  // Center the signature on the document
  const centerSignature = () => {
    if (documentRef.current) {
      const docRect = documentRef.current.getBoundingClientRect();
      setSignaturePosition({
        x: docRect.width / 2 - 100,
        y: docRect.height / 2 - 70 // Increased to accommodate ID bar
      });
    }
  };
  
  // Error handling for missing document data
  useEffect(() => {
    if (!documentData) {
      setError('No document data provided');
    } else if (!documentData.url) {
      setError('Document URL is missing');
    }
  }, [documentData]);
  
  // Handle page navigation
  const changePage = (delta) => {
    const newPage = currentPage + delta;
    if (numPages && newPage >= 1 && newPage <= numPages) {
      setCurrentPage(newPage);
      // Reset positioned flags when changing pages
      initializedRef.current = false;
    }
  };
  
  // Handle zoom adjustments
  const changeZoom = (delta) => {
    const newZoom = Math.max(0.5, Math.min(2, zoom + delta));
    setZoom(newZoom);
  };
  
  // Create a new signature
  const createSignature = () => {
    setShowSignatureCanvas(true);
  };
  
  // Handle signature created from SignatureCanvas component
  const handleSignatureCreated = (data, type) => {
    console.log('SignaturePlacement handleSignatureCreated called');
    console.log('selectedField:', selectedField);
    console.log('signaturePosition:', signaturePosition);
    console.log('activeFieldId:', activeFieldId);
    console.log('PDF dimensions:', pdfPageDimensions);
    
    // Calculate normalized coordinates
    const calculateCoordinates = (position, width, height) => {
      if (!pdfPageDimensions) {
        console.warn('PDF dimensions not available, using legacy format');
        return {
          x: position.x,
          y: position.y,
          width: width || 200,
          height: height || 70,
          coordinateFormat: 'v1'
        };
      }
      
      // Get the actual rendered width (800px on desktop)
      const renderedWidth = window.innerWidth > 1024 ? 800 : (renderedPageDimensions?.width || 800);
      const scaleFactor = pdfPageDimensions.width / renderedWidth;
      
      // Convert pixel coordinates to PDF points
      const pdfX = position.x * scaleFactor;
      const pdfY = position.y * scaleFactor;
      const pdfWidth = (width || 200) * scaleFactor;
      const pdfHeight = (height || 70) * scaleFactor;
      
      // Calculate normalized coordinates
      const normalizedX = pdfX / pdfPageDimensions.width;
      const normalizedY = pdfY / pdfPageDimensions.height;
      const normalizedWidth = pdfWidth / pdfPageDimensions.width;
      const normalizedHeight = pdfHeight / pdfPageDimensions.height;
      
      console.log(`📐 Signature coordinate conversion:`);
      console.log(`   Pixel: (${position.x}, ${position.y}) ${width}x${height}`);
      console.log(`   PDF points: (${pdfX.toFixed(2)}, ${pdfY.toFixed(2)}) ${pdfWidth.toFixed(2)}x${pdfHeight.toFixed(2)}`);
      console.log(`   Normalized: (${normalizedX.toFixed(3)}, ${normalizedY.toFixed(3)}) ${normalizedWidth.toFixed(3)}x${normalizedHeight.toFixed(3)}`);
      
      return {
        // PDF points
        x: pdfX,
        y: pdfY,
        width: pdfWidth,
        height: pdfHeight,
        // Normalized coordinates
        normalizedX,
        normalizedY,
        normalizedWidth,
        normalizedHeight,
        // Original pixel coordinates
        pixelX: position.x,
        pixelY: position.y,
        pixelWidth: width || 200,
        pixelHeight: height || 70,
        // Metadata
        renderedPageWidth: renderedWidth,
        pdfPageWidth: pdfPageDimensions.width,
        pdfPageHeight: pdfPageDimensions.height,
        coordinateFormat: 'v2'
      };
    };
    
    // Determine position to use
    const position = {
      x: selectedField?.x !== undefined ? selectedField.x : signaturePosition.x,
      y: selectedField?.y !== undefined ? selectedField.y : signaturePosition.y
    };
    const width = selectedField?.width || 200;
    const height = selectedField?.height || 70;
    
    // Format the signature data for the callback
    const signatureWithPosition = {
      signatureData: data,
      signatureType: type,
      page: currentPage,
      position: calculateCoordinates(position, width, height),
      fieldId: selectedField?.id || activeFieldId
    };
    
    console.log('Formatted signature data:', signatureWithPosition);
    
    // Call the callback immediately with the signature data
    onSignatureAdded(signatureWithPosition);
  };
  
  // Handle mousedown for signature dragging
  const handleMouseDown = (e) => {
    if (!signatureData) return;
    
    const signature = signatureRef.current;
    if (signature && (e.target === signature || signature.contains(e.target))) {
      setIsDragging(true);
      
      const rect = signature.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      
      e.preventDefault();
    }
  };
  
  // Handle mousemove for signature dragging
  const handleMouseMove = (e) => {
    if (isDragging && signatureRef.current && documentRef.current) {
      const docRect = documentRef.current.getBoundingClientRect();
      
      // Calculate new position, ensuring signature stays within document bounds
      const newX = Math.max(0, Math.min(
        e.clientX - docRect.left - dragOffset.x,
        docRect.width - signatureRef.current.offsetWidth
      ));
      
      const newY = Math.max(0, Math.min(
        e.clientY - docRect.top - dragOffset.y,
        docRect.height - signatureRef.current.offsetHeight
      ));
      
      setSignaturePosition({ x: newX, y: newY });
      
      // Check if we're now inside a signature field
      if (prePlacedFields.length > 0) {
        const fieldsOnCurrentPage = prePlacedFields.filter(field => 
          (field.page === currentPage) || (field.page === undefined && currentPage === 1)
        );
        
        let closestField = null;
        let closestDistance = 100; // Minimum proximity threshold
        
        fieldsOnCurrentPage.forEach(field => {
          const fieldX = field.x || 0;
          const fieldY = field.y || 0;
              const fieldWidth = field.width || 200;
            const fieldHeight = field.height || 70; // Increased to accommodate ID bar
          
          // Check if signature center is inside field
          const signatureCenterX = newX + (signatureRef.current.offsetWidth / 2);
          const signatureCenterY = newY + (signatureRef.current.offsetHeight / 2);
          
          const distance = Math.sqrt(
            Math.pow(signatureCenterX - (fieldX + fieldWidth/2), 2) + 
            Math.pow(signatureCenterY - (fieldY + fieldHeight/2), 2)
          );
          
          if (distance < closestDistance) {
            closestDistance = distance;
            closestField = field;
          }
        });
        
        if (closestField) {
          setActiveFieldId(closestField.id);
        } else {
          setActiveFieldId(null);
        }
      }
    }
  };
  
  // Handle mouseup to stop dragging
  const handleMouseUp = () => {
    setIsDragging(false);
    
    // Snap to active field if one is selected
    if (activeFieldId && prePlacedFields.length > 0) {
      const activeField = prePlacedFields.find(field => field.id === activeFieldId);
      if (activeField) {
        setSignaturePosition({
          x: activeField.x || 0,
          y: activeField.y || 0
        });
      }
    }
  };
  
  // Apply signature to document with current position and page
  const applySignature = () => {
    if (!signatureData) {
      setError('Please create a signature first');
      return;
    }
    
    // If we have an active field, use that field's data
    if (activeFieldId && prePlacedFields.length > 0) {
      const activeField = prePlacedFields.find(field => field.id === activeFieldId);
      if (activeField) {
        onSignatureAdded({
          signatureData,
          signatureType,
          fieldId: activeFieldId,
          position: {
            x: activeField.x || 0,
            y: activeField.y || 0
          },
          page: currentPage
        });
        return;
      }
    }
    
    // Default to current position
    onSignatureAdded({
      signatureData,
      signatureType,
      position: signaturePosition,
      page: currentPage
    });
  };
  
  // Handle clicks on signature fields
  const handleFieldClick = (field) => {
    if (!signatureData) {
      // If no signature, open signature canvas immediately
      setShowSignatureCanvas(true);
      setActiveFieldId(field.id);
    } else {
      // If already have signature, move it to this field
      setSignaturePosition({
        x: field.x || 0,
        y: field.y || 0
      });
      setActiveFieldId(field.id);
    }
  };
  
  // PDF document loaded successfully
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };
  
  // Add handler to track PDF page dimensions
  const onPageLoadSuccess = (page) => {
    const viewport = page.getViewport({ scale: 1 });
    setPdfPageDimensions({
      width: viewport.width,
      height: viewport.height
    });
    console.log(`📐 PDF Page dimensions: ${viewport.width}x${viewport.height} points`);
    
    // Track rendered dimensions
    setTimeout(() => {
      const pageEl = documentRef.current?.querySelector('.react-pdf__Page');
      if (pageEl) {
        const rect = pageEl.getBoundingClientRect();
        setRenderedPageDimensions({
          width: rect.width / zoom,
          height: rect.height / zoom
        });
        console.log(`📐 Rendered dimensions: ${rect.width / zoom}x${rect.height / zoom}px`);
      }
    }, 100);
  };
  
  // Set up event listeners for mouse events
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, prePlacedFields, currentPage, activeFieldId]);
  
  // Fetch signature data from API
  const fetchSignatureFields = async () => {
    if (!documentId) return;
    
    try {
      // Check if there's a signature request ID in the URL
      const params = new URLSearchParams(window.location.search);
      const requestId = params.get('requestId');
      
      if (requestId) {
        // Try to get signature fields from the request
        try {
          const viewResponse = await axios.patch(
            `${API_URL}/documents/signatures/${requestId}/view`,
            {},
            {
              headers: {
                'Authorization': `Bearer ${user?.token}`
              }
            }
          );
          
          console.log('Signature request view response:', viewResponse.data);
          
          // If there are signature fields in the response, use them
          if (viewResponse.data.signatureFields && viewResponse.data.signatureFields.length > 0) {
            console.log('Found signature fields in request:', viewResponse.data.signatureFields);
            setPrePlacedFields(viewResponse.data.signatureFields);
          }
        } catch (err) {
          console.error('Error viewing signature request:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching signature data:', error);
    }
  };
  
  // Get signature image URL helper function
  const getSignatureImageUrl = (signatureData) => {
    // If signatureData is a base64 string (fresh signature not yet saved), return it directly
    if (signatureData && signatureData.startsWith('data:image')) {
      return signatureData;
    }
    
    // If signatureData is a URL or path (retrieved from storage), construct proper URL
    if (signatureData && typeof signatureData === 'string') {
      // Check if it's already a full URL
      if (signatureData.startsWith('http')) {
        return signatureData;
      }
      // If it's just a file path, construct API URL for signature retrieval
      return `${API_URL}/documents/signature/${signatureData}/image`;
    }
    
    // Fallback
    return '/placeholder-signature.png';
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Signature canvas - shown immediately */}
        {showSignatureCanvas && (
          <div className="w-full h-full">
            <SignatureCanvas
              onSignatureCreated={handleSignatureCreated}
              onCancel={onCancel}
              initialSignatureType={signatureType}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SignaturePlacement;